import { Box, Flex, Text } from "@radix-ui/themes";
import ProductSkeleton from "./product-skeleton";
import { CartItem } from "@/features/annotation/lib/types";
import { ErrorData, OrderLine } from "@/shared/api/lod-api";
import { ErrorMessage } from "./error-message";
import { CartColumn } from "./cart-column";
import ProductItem from "./product-item";
import CartIsEmpty from "./cart-is-empty";

interface PredictionColumnProps {
  predictionCartItem?: OrderLine;
  isPredictionLoading?: boolean;
  isQuantityDifferent?: boolean;
  predictionError?: ErrorData | null;
  isPredictionCartEmpty?: boolean;
  isFirstRow?: boolean;
}

const PredictionColumn = ({
  predictionCartItem,
  isPredictionLoading,
  isQuantityDifferent,
  predictionError,
  isPredictionCartEmpty,
  isFirstRow,
}: PredictionColumnProps) => {
  const renderContent = () => {
    if (predictionError) {
      return (
        <Box className="mx-3">
          <ErrorMessage error={predictionError} />
        </Box>
      );
    }

    if (isPredictionLoading) {
      return (
        <Box className="mx-3">
          <ProductSkeleton count={1} />
        </Box>
      );
    }

    if (predictionCartItem) {
      return (
        <Box className="mx-3">
          <ProductItem
            name={predictionCartItem.name}
            imageUrl={predictionCartItem.image_url}
            barcode={predictionCartItem.barcode}
            skuId={predictionCartItem.sku_base_id}
            quantity={predictionCartItem.quantity}
            isQuantityNegative={predictionCartItem.quantity < 0}
            isQuantityDifferent={isQuantityDifferent}
            unitOfMeasurement={predictionCartItem.uom}
          />
        </Box>
      );
    }

    if (!isPredictionCartEmpty) {
      return (
        <Flex justify="center" align="center" className="h-20 mx-3">
          <Text size="1" color="gray">
            No matching data
          </Text>
        </Flex>
      );
    }

    if (isFirstRow) {
      return (
        <Box className="mx-3">
          <CartIsEmpty />
        </Box>
      );
    }

    return null;
  };

  return (
    <div className="w-1/3 min-w-1/3 py-2 align-top px-0">{renderContent()}</div>
  );
};

interface ComparisonRowProps {
  annotatedCartItem?: CartItem;
  orderProduct?: OrderLine;
  predictionCartItem?: OrderLine;
  isPredictionLoading?: boolean;
  isQuantityDifferent?: boolean;
  predictionError?: ErrorData | null;
  isAnnotatedCartEmpty?: boolean;
  isOrderCartEmpty?: boolean;
  isPredictionCartEmpty?: boolean;
  isFirstRow?: boolean;
}

export const ComparisonRow = ({
  annotatedCartItem,
  orderProduct,
  predictionCartItem,
  isPredictionLoading = false,
  isQuantityDifferent = false,
  predictionError,
  isAnnotatedCartEmpty = false,
  isOrderCartEmpty = false,
  isPredictionCartEmpty = false,
  isFirstRow = false,
}: ComparisonRowProps) => {
  return (
    <div className="flex border-b">
      <div className="w-1/3 min-w-1/3 border-r align-top py-2 px-0">
        <CartColumn
          item={annotatedCartItem}
          isCartEmpty={isAnnotatedCartEmpty}
          isFirstRow={isFirstRow}
          isQuantityDifferent={isQuantityDifferent}
          type="annotated"
        />
      </div>
      <div className="w-1/3 min-w-1/3 border-r align-top py-2 px-0">
        <CartColumn
          item={orderProduct}
          isCartEmpty={isOrderCartEmpty}
          isFirstRow={isFirstRow}
          isQuantityDifferent={isQuantityDifferent}
          type="order"
        />
      </div>
      <PredictionColumn
        predictionCartItem={predictionCartItem}
        isPredictionLoading={isPredictionLoading}
        isQuantityDifferent={isQuantityDifferent}
        predictionError={predictionError}
        isPredictionCartEmpty={isPredictionCartEmpty}
        isFirstRow={isFirstRow}
      />
    </div>
  );
};
